import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  TablePagination
} from '@mui/material';
import { Refresh, CheckCircle, Schedule, Error as ErrorIcon, HourglassEmpty } from '@mui/icons-material';
import { format } from 'date-fns';
import { authGet } from '../../services/apiService';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'default', icon: <Schedule fontSize="small" /> },
  processing: { label: 'Processing', color: 'info', icon: <HourglassEmpty fontSize="small" /> },
  completed: { label: 'Completed', color: 'success', icon: <CheckCircle fontSize="small" /> },
  failed: { label: 'Failed', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  skipped: { label: 'Skipped', color: 'default', icon: <CheckCircle fontSize="small" /> }
};

/**
 * Processed = completed + skipped.
 * Not processed = pending + processing + failed.
 */
const WebhookQueueStatus = () => {
  const [statusData, setStatusData] = useState(null);
  const [queueData, setQueueData] = useState({ events: [], totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('failed'); // '' = all; default to failed
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadStatus = useCallback(async () => {
    try {
      const res = await authGet('/api/ado-webhook/status', null, 'json', { useCache: false });
      if (res && res.success) {
        setStatusData({
          processingStats: res.processingStats,
          queueStatus: res.queueStatus,
          timestamp: res.timestamp
        });
      } else {
        setStatusData(null);
      }
    } catch (err) {
      console.error('Error loading webhook status:', err);
      setError(err.message || 'Failed to load status');
      setStatusData(null);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      setQueueLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', String(rowsPerPage));
      params.set('skip', String(page * rowsPerPage));
      const url = `/api/ado-webhook/queue?${params.toString()}`;
      const res = await authGet(url, null, 'json', { useCache: false });
      if (res && res.success) {
        setQueueData({
          events: res.events || [],
          totalCount: res.totalCount != null ? res.totalCount : (res.events || []).length
        });
      } else {
        setQueueData({ events: [], totalCount: 0 });
      }
    } catch (err) {
      console.error('Error loading queue:', err);
      setQueueData({ events: [], totalCount: 0 });
    } finally {
      setQueueLoading(false);
    }
  }, [statusFilter, page, rowsPerPage]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      await loadStatus();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [loadStatus]);

  useEffect(() => {
    if (!loading) loadQueue();
  }, [loading, loadQueue]);

  const handleRefresh = () => {
    setError(null);
    loadStatus().then(() => loadQueue());
  };

  const handleStatusFilter = (e, value) => {
    setStatusFilter(value || '');
    setPage(0);
  };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const queueCounts = (() => {
    const webhook = statusData?.queueStatus?.webhook;
    if (!webhook) return {};
    const fromBreakdown = (webhook.breakdown || []).reduce((acc, item) => {
      acc[item._id] = item.count;
      return acc;
    }, {});
    return {
      pending: webhook.pending ?? fromBreakdown.pending ?? 0,
      processing: fromBreakdown.processing ?? 0,
      completed: fromBreakdown.completed ?? 0,
      failed: webhook.failed ?? fromBreakdown.failed ?? 0,
      skipped: fromBreakdown.skipped ?? 0
    };
  })();
  const processedCount = queueCounts.completed + queueCounts.skipped;
  const notProcessedCount = queueCounts.pending + queueCounts.processing + queueCounts.failed;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Processing Queue (Processed vs Not Processed)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Jobs from the webhook event queue. Processed = completed or skipped; Not processed = pending, in progress, or failed.
        {' '}
        <strong>ADO ID</strong> is the Azure DevOps work item id from the event payload—copy it to look up the item in ADO or trigger a manual sync.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
          <Typography variant="overline" color="text.secondary">Processed</Typography>
          <Typography variant="h4" color="success.main">
            {loading ? '—' : processedCount}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            completed + skipped
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
          <Typography variant="overline" color="text.secondary">Not processed</Typography>
          <Typography variant="h4" color="warning.main">
            {loading ? '—' : notProcessedCount}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            pending + processing + failed
          </Typography>
        </Paper>
        {!loading && (statusData?.queueStatus?.webhook != null) && (
          <>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
              <Typography variant="overline" color="text.secondary">Pending</Typography>
              <Typography variant="h5">{queueCounts.pending ?? 0}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
              <Typography variant="overline" color="text.secondary">Processing</Typography>
              <Typography variant="h5">{queueCounts.processing ?? 0}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
              <Typography variant="overline" color="text.secondary">Failed</Typography>
              <Typography variant="h5" color="error.main">{queueCounts.failed ?? 0}</Typography>
            </Paper>
          </>
        )}
      </Box>

      {/* Queue table */}
      <Paper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, flexWrap: 'wrap', gap: 1 }}>
          <ToggleButtonGroup value={statusFilter} exclusive onChange={handleStatusFilter} size="small">
            <ToggleButton value="">All</ToggleButton>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <ToggleButton key={key} value={key}>
                {cfg.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading || queueLoading} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>ADO ID</TableCell>
                <TableCell>Work Item</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Priority</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Processed / Completed</TableCell>
                <TableCell>Retries</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queueLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : !queueData.events.length ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }} color="text.secondary">
                    No queue events match the filter.
                  </TableCell>
                </TableRow>
              ) : (
                queueData.events.map((ev) => {
                  const cfg = STATUS_CONFIG[ev.status] || { label: ev.status, color: 'default' };
                  return (
                    <TableRow key={ev.eventId || ev._id}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {(ev.eventId || '').slice(0, 32)}{(ev.eventId && ev.eventId.length > 32) ? '…' : ''}
                      </TableCell>
                      <TableCell>{ev.eventType || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', userSelect: 'all' }}>
                        {ev.adoWorkItemId || ev.workItemId || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                        {ev.workItemId || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{ev.priority ?? '—'}</TableCell>
                      <TableCell>
                        {ev.createdAt ? format(new Date(ev.createdAt), 'yyyy-MM-dd HH:mm:ss') : '—'}
                      </TableCell>
                      <TableCell>
                        {ev.processedAt && format(new Date(ev.processedAt), 'HH:mm:ss')}
                        {ev.completedAt && ` → ${format(new Date(ev.completedAt), 'HH:mm:ss')}`}
                        {!ev.processedAt && !ev.completedAt && '—'}
                      </TableCell>
                      <TableCell>{ev.retryCount ?? 0}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={queueData.totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:"
        />
      </Paper>
    </Box>
  );
};

export default WebhookQueueStatus;
